/* eslint-disable relay/unused-fields */
import type { FunctionComponent } from "react";
import { useMemo } from "react";
import type { PreloadedQuery } from "react-relay";
import { graphql, useFragment, usePreloadedQuery } from "react-relay";

import { useSearchContext } from "~/components/search/contexts";
import { query } from "~/components/search/search-result-panel";
import type { searchResultListArticleSearchQuery$key } from "~/queries/__generated__/searchResultListArticleSearchQuery.graphql";
import type { searchResultListSectionSearchConnection$key } from "~/queries/__generated__/searchResultListSectionSearchConnection.graphql";
import type { searchResultListTopicSearchConnection$key } from "~/queries/__generated__/searchResultListTopicSearchConnection.graphql";
import type { searchResultPanelQuery } from "~/queries/__generated__/searchResultPanelQuery.graphql";

import { DateRangeMenu } from "./advanced-filter-options/date-range-menu";
import { SectionMenu } from "./advanced-filter-options/section-menu";
import { ArticleSearchResultList } from "./article-search-result-list";
import { SectionSearchResultList } from "./section-search-result-list";
import {
  AdvancedFilterOptionsContainer,
  HeaderContainer,
  NoResultsContainer,
  NoResultsContent,
  NoResultsHeader,
  ResultAndSortingContainer,
  StyledSearchResultListSortingMenu,
  TotalCount,
  TotalResultText,
} from "./styles";
import { TopicSearchResultList } from "./topic-search-result-list";

export type Props = {
  queryReference: PreloadedQuery<searchResultPanelQuery>;
};

export const SearchResultList: FunctionComponent<Props> = ({ queryReference }) => {
  const data = usePreloadedQuery(query, queryReference);

  const articles = useFragment<searchResultListArticleSearchQuery$key>(
    graphql`
      fragment searchResultListArticleSearchQuery on Query {
        articleSearch(
          filter: {
            query: $query
            sectionIds: $sectionIds
            startDate: $startDate
            endDate: $endDate
          }
          orderBy: $orderBy
          first: $first
          after: $after
        ) {
          edges {
            node {
              __typename
            }
          }
          totalCount
        }
        ...articleSearchResultListQuery
      }
    `,
    data,
  );

  const topics = useFragment<searchResultListTopicSearchConnection$key>(
    graphql`
      fragment searchResultListTopicSearchConnection on TopicSearchConnection {
        edges {
          node {
            __typename
          }
        }
        ...topicSearchResultListTopicSearchConnection
      }
    `,
    data.topicSearch,
  );

  const sections = useFragment<searchResultListSectionSearchConnection$key>(
    graphql`
      fragment searchResultListSectionSearchConnection on SectionSearchConnection {
        edges {
          node {
            __typename
          }
        }
        ...sectionSearchResultListSectionSearchConnection
      }
    `,
    data.sectionSearch,
  );

  const noResult = useMemo(
    () =>
      [articles.articleSearch?.edges.length, topics?.edges.length, sections?.edges.length].every(
        edgeLength => !edgeLength,
      ),
    [articles.articleSearch?.edges.length, sections?.edges.length, topics?.edges.length],
  );

  const { currentValue } = useSearchContext();

  return (
    <>
      {noResult ? (
        <NoResultsContainer>
          <NoResultsHeader>Sorry! There are no results for your search term.</NoResultsHeader>
          <NoResultsContent>
            Please check if your spelling is correct, or try a different word or phrase.
          </NoResultsContent>
        </NoResultsContainer>
      ) : (
        <HeaderContainer>
          <AdvancedFilterOptionsContainer>
            <DateRangeMenu />
            <SectionMenu />
          </AdvancedFilterOptionsContainer>
          <ResultAndSortingContainer>
            <TotalResultText>
              About <TotalCount>{articles.articleSearch?.totalCount}</TotalCount> results for{" "}
              {`"${currentValue}"`}
            </TotalResultText>
            <StyledSearchResultListSortingMenu />
          </ResultAndSortingContainer>
        </HeaderContainer>
      )}
      {sections && <SectionSearchResultList reference={sections} />}
      {topics && <TopicSearchResultList reference={topics} />}
      {articles && <ArticleSearchResultList reference={articles} />}
    </>
  );
};

SearchResultList.displayName = "SearchResultList";
